/**
 * Trawl Survey data layer - full UI: season/year, cruises, species dropdown,
 * time of day, depth and bottom temp sliders, MapController filters and data.
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import type { RegistryComponentProps } from '../../../pages/registry/componentRegistry';
import { useMap } from '../MapContext';
import { useApi } from '../../../api/api';
import RangeSlider from '../../../components/common/RangeSlider/RangeSlider';

/** Species from GET /api/trawl-survey/species (com_name, sci_name, species_uuid) */
interface TrawlSurveySpeciesItem {
  com_name?: string;
  sci_name?: string;
  species_uuid?: string;
  [key: string]: unknown;
}

/** MapController instance from MapContext (MapContext.jsx has no TS types). */
interface MapControllerLike {
  isReady: () => boolean;
  setFilter: (name: string, value: unknown) => void;
  setTrawlSurveyData: (data: { filtered: unknown; unfiltered: unknown } | null) => Promise<void>;
  isLayerActive: (slug: string) => boolean;
  addLayer: (slug: string) => Promise<void>;
}

interface CruiseItem {
  season?: string;
  year?: string | number;
  cruise6?: string;
}

/** Layout component_default_values for trawl-survey (flat or nested under default). */
interface TrawlDefaultValues {
  default?: {
    season?: string;
    year?: string | number;
    species_uuid?: string;
    time_of_day?: string;
    depth_min?: number;
    depth_max?: number;
    bot_temp_min?: number;
    bot_temp_max?: number;
  };
  season?: string;
  year?: string | number;
  species_uuid?: string;
  time_of_day?: string;
  depth_min?: number;
  depth_max?: number;
  bot_temp_min?: number;
  bot_temp_max?: number;
}

function getTrawlDefault<T>(dv: TrawlDefaultValues | null | undefined, key: keyof NonNullable<TrawlDefaultValues['default']>): T | undefined {
  if (!dv) return undefined;
  const v = (dv as Record<string, unknown>)[key] ?? (dv.default as Record<string, unknown>)?.[key];
  return v as T | undefined;
}

interface TrawlInjections {
  getTrawlSurveyCruises?: () => Promise<{ data?: CruiseItem[] }>;
  getTrawlSurveyCatchesByCruise?: (cruise6: string) => Promise<{ data?: unknown }>;
  onTrawlCatchesChange?: (data: { filtered: unknown; unfiltered: unknown } | null) => void;
  timeOfDayFilter?: string;
  setTimeOfDayFilter?: (v: string) => void;
  tempDepthRange?: { min: number; max: number };
  setTempDepthRange?: (v: { min: number; max: number }) => void;
  setDepthRange?: (v: { min: number; max: number }) => void;
  tempBotTempRange?: { min: number; max: number };
  setTempBotTempRange?: (v: { min: number; max: number }) => void;
  setBotTempRange?: (v: { min: number; max: number }) => void;
  dataLoading?: boolean;
  catchabilityLoading?: boolean;
  stockSummaryLoading?: boolean;
}

export default function TrawlSurveyDataLayer(
  props: RegistryComponentProps
): React.ReactElement {
  const { injections = {}, defaultValues } = props;
  const dv = defaultValues as TrawlDefaultValues | null | undefined;
  const { layerController } = useMap() as { layerController: MapControllerLike | null };
  const mapController = layerController;
  const { getTrawlSurveySpecies } = useApi();
  const {
    getTrawlSurveyCruises,
    getTrawlSurveyCatchesByCruise,
    onTrawlCatchesChange,
    timeOfDayFilter = '',
    setTimeOfDayFilter = () => {},
    tempDepthRange = { min: 0, max: 200 },
    setTempDepthRange = () => {},
    setDepthRange = () => {},
    tempBotTempRange = { min: -10, max: 30 },
    setTempBotTempRange = () => {},
    setBotTempRange = () => {},
    dataLoading = false,
    catchabilityLoading = false,
    stockSummaryLoading = false,
  } = injections as TrawlInjections;

  const [trawlCruises, setTrawlCruises] = useState<CruiseItem[] | null>(null);
  const [trawlCruisesLoading, setTrawlCruisesLoading] = useState(false);
  const [trawlSeason, setTrawlSeason] = useState(() => {
    const s = getTrawlDefault(dv, 'season');
    return s != null && s !== '' ? String(s).toUpperCase() : '';
  });
  const [trawlYear, setTrawlYear] = useState(() => {
    const y = getTrawlDefault(dv, 'year');
    return y != null && y !== '' ? String(y) : '';
  });
  const [selectedCruise6, setSelectedCruise6] = useState<string | null>(null);
  const [trawlCatches, setTrawlCatches] = useState<Record<string, unknown[]> | unknown[] | null>(null);
  const [trawlCatchesLoading, setTrawlCatchesLoading] = useState(false);
  const [trawlSurveySpecies, setTrawlSurveySpecies] = useState<TrawlSurveySpeciesItem[]>([]);
  const [trawlSurveySpeciesLoading, setTrawlSurveySpeciesLoading] = useState(false);
  const [selectedSpeciesUuid, setSelectedSpeciesUuid] = useState<string>('');

  // Sync from layout defaults when they change (e.g. page navigation). Use ref to avoid overwriting user changes.
  const defaultSeason = getTrawlDefault(dv, 'season');
  const defaultYear = getTrawlDefault(dv, 'year');
  const defaultSpeciesUuid = getTrawlDefault(dv, 'species_uuid');
  const lastAppliedDefaultsRef = useRef<string | null>(null);
  useEffect(() => {
    const key = `${defaultSeason ?? ''}|${defaultYear ?? ''}|${defaultSpeciesUuid ?? ''}`;
    if (key === lastAppliedDefaultsRef.current) return;
    lastAppliedDefaultsRef.current = key;
    if (defaultSeason != null && defaultSeason !== '') setTrawlSeason(String(defaultSeason).toUpperCase());
    if (defaultYear != null && defaultYear !== '') setTrawlYear(String(defaultYear));
    // species_uuid is applied after dropdown populates (see effect below)
  }, [defaultSeason, defaultYear, defaultSpeciesUuid]);

  // Fetch trawl survey species on mount
  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        setTrawlSurveySpeciesLoading(true);
        const resp = await getTrawlSurveySpecies?.();
        if (!isMounted) return;
        const raw = resp?.data;
        const list = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' && Array.isArray((raw as { results?: unknown }).results)) ? (raw as { results: TrawlSurveySpeciesItem[] }).results : [];
        const withUuid = list.filter((r: TrawlSurveySpeciesItem) => r.species_uuid != null && r.species_uuid !== '');
        setTrawlSurveySpecies(withUuid);
      } catch (e) {
        if (!isMounted) return;
        setTrawlSurveySpecies([]);
        console.error('Failed to load trawl survey species', e);
      } finally {
        if (isMounted) setTrawlSurveySpeciesLoading(false);
      }
    };
    load();
    return () => { isMounted = false; };
  }, [getTrawlSurveySpecies]);

  // Apply species_uuid default after dropdown populates (species list has loaded)
  const lastAppliedSpeciesDefaultRef = useRef<string | null>(null);
  useEffect(() => {
    if (trawlSurveySpeciesLoading) return;
    if (!defaultSpeciesUuid || defaultSpeciesUuid === '') {
      lastAppliedSpeciesDefaultRef.current = null;
      return;
    }
    if (lastAppliedSpeciesDefaultRef.current === defaultSpeciesUuid) return;
    const uuid = String(defaultSpeciesUuid);
    const exists = trawlSurveySpecies.some((s) => s.species_uuid === uuid);
    if (exists) {
      lastAppliedSpeciesDefaultRef.current = uuid;
      setSelectedSpeciesUuid(uuid);
    }
  }, [trawlSurveySpeciesLoading, trawlSurveySpecies, defaultSpeciesUuid]);

  const tempDepthRangeRef = useRef(tempDepthRange);
  const tempBotTempRangeRef = useRef(tempBotTempRange);
  const prevDepthRangeRef = useRef<{ min: number | null; max: number | null }>({ min: null, max: null });
  const prevBotTempRangeRef = useRef<{ min: number | null; max: number | null }>({ min: null, max: null });

  useEffect(() => {
    tempDepthRangeRef.current = tempDepthRange;
  }, [tempDepthRange]);
  useEffect(() => {
    tempBotTempRangeRef.current = tempBotTempRange;
  }, [tempBotTempRange]);

  const availableSeasons = useMemo(() => {
    if (!trawlCruises?.length) return [];
    const unique = new Set<string>();
    trawlCruises.forEach((c) => {
      const season = (c.season || '').toUpperCase();
      if (season && (!trawlYear || Number(c.year) === Number(trawlYear))) unique.add(season);
    });
    return Array.from(unique).sort();
  }, [trawlCruises, trawlYear]);

  const availableYears = useMemo(() => {
    if (!trawlCruises?.length) return [];
    const unique = new Set<number>();
    trawlCruises.forEach((c) => {
      const year = Number(c.year);
      const season = (c.season || '').toUpperCase();
      if (year && (!trawlSeason || season === (trawlSeason || '').toUpperCase())) unique.add(year);
    });
    return Array.from(unique).sort((a, b) => b - a);
  }, [trawlCruises, trawlSeason]);

  const isTrawlSelectionValid = !!(
    trawlCruises?.length &&
    trawlSeason &&
    trawlYear &&
    trawlCruises.some(
      (c) =>
        (c.season || '').toUpperCase() === (trawlSeason || '').toUpperCase() &&
        Number(c.year) === Number(trawlYear)
    )
  );

  const selectedSpeciesObj = useMemo(() => {
    if (!selectedSpeciesUuid || !trawlSurveySpecies?.length) return null;
    return trawlSurveySpecies.find((s) => s.species_uuid === selectedSpeciesUuid) ?? null;
  }, [selectedSpeciesUuid, trawlSurveySpecies]);

  const filterTrawlCatchesBySpecies = useCallback(
    (catchesData: Record<string, unknown[]> | unknown[] | null): Record<string, unknown[]> | unknown[] | null => {
      if (!selectedSpeciesUuid || !selectedSpeciesObj || !catchesData) return catchesData;
      const matchName = (selectedSpeciesObj.com_name ?? selectedSpeciesObj.sci_name ?? '').trim().toLowerCase();
      if (!matchName) return catchesData;
      const filtered: Record<string, unknown[]> = {};
      if (catchesData && typeof catchesData === 'object' && !Array.isArray(catchesData)) {
        Object.entries(catchesData).forEach(([stationId, catches]) => {
          if (!Array.isArray(catches)) return;
          const match = (catches as { species_name?: string; com_name?: string; common_name?: string; sci_name?: string }[]).filter((c) => {
            const cName = (c.species_name ?? c.com_name ?? c.common_name ?? c.sci_name ?? '').trim().toLowerCase();
            return cName === matchName;
          });
          if (match.length > 0) filtered[stationId] = match;
        });
      }
      return filtered;
    },
    [selectedSpeciesUuid, selectedSpeciesObj]
  );

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        setTrawlCruisesLoading(true);
        const resp = await getTrawlSurveyCruises?.();
        if (!isMounted) return;
        setTrawlCruises(resp?.data ?? []);
      } catch (e) {
        if (!isMounted) return;
        setTrawlCruises([]);
        console.error('Failed to load trawl survey cruises', e);
      } finally {
        if (isMounted) setTrawlCruisesLoading(false);
      }
    };
    if (getTrawlSurveyCruises) load();
    return () => {
      isMounted = false;
    };
  }, [getTrawlSurveyCruises]);

  useEffect(() => {
    if (!mapController?.isReady()) return;
    mapController.setFilter('trawlSurveySeason', trawlSeason || null);
  }, [mapController, trawlSeason]);

  useEffect(() => {
    if (!mapController?.isReady()) return;
    mapController.setFilter('trawlSurveyYear', trawlYear || null);
  }, [mapController, trawlYear]);

  useEffect(() => {
    if (!mapController?.isReady()) return;
    mapController.setFilter('trawlSurveySpeciesFilter', selectedSpeciesUuid || null);
  }, [mapController, selectedSpeciesUuid]);

  useEffect(() => {
    if (!mapController?.isReady()) return;
    mapController.setFilter('trawlSurveyTimeOfDay', timeOfDayFilter || null);
  }, [mapController, timeOfDayFilter]);

  useEffect(() => {
    if (!mapController?.isReady()) return;
    if (
      prevDepthRangeRef.current.min !== tempDepthRange.min ||
      prevDepthRangeRef.current.max !== tempDepthRange.max
    ) {
      prevDepthRangeRef.current = { min: tempDepthRange.min, max: tempDepthRange.max };
      mapController.setFilter('trawlSurveyMinDepth', tempDepthRange.min);
      mapController.setFilter('trawlSurveyMaxDepth', tempDepthRange.max);
    }
  }, [mapController, tempDepthRange.min, tempDepthRange.max]);

  useEffect(() => {
    if (!mapController?.isReady()) return;
    if (
      prevBotTempRangeRef.current.min !== tempBotTempRange.min ||
      prevBotTempRangeRef.current.max !== tempBotTempRange.max
    ) {
      prevBotTempRangeRef.current = { min: tempBotTempRange.min, max: tempBotTempRange.max };
      mapController.setFilter('trawlSurveyMinBotTemp', tempBotTempRange.min);
      mapController.setFilter('trawlSurveyMaxBotTemp', tempBotTempRange.max);
    }
  }, [mapController, tempBotTempRange.min, tempBotTempRange.max]);

  useEffect(() => {
    if (!isTrawlSelectionValid) {
      setSelectedCruise6(null);
      setTrawlCatches(null);
      setSelectedSpeciesUuid('');
      onTrawlCatchesChange?.(null);
      if (mapController?.isReady()) {
        mapController.setTrawlSurveyData(null);
      }
      return;
    }
    const match = trawlCruises?.find(
      (c) =>
        (c.season || '').toUpperCase() === (trawlSeason || '').toUpperCase() &&
        Number(c.year) === Number(trawlYear)
    );
    const cruise6 = match?.cruise6;
    if (!cruise6) return;
    if (selectedCruise6 === cruise6 && trawlCatches) return;

    const isSwitching = selectedCruise6 !== null && selectedCruise6 !== cruise6;
    if (isSwitching) {
      setTrawlCatches(null);
      onTrawlCatchesChange?.(null);
    }

    let isMounted = true;
    const loadCatches = async () => {
      try {
        setTrawlCatchesLoading(true);
        setSelectedCruise6(cruise6);
        const resp = await getTrawlSurveyCatchesByCruise?.(cruise6);
        if (!isMounted) return;
        let catchesData: Record<string, unknown[]> | unknown[] | null =
          (resp?.data as Record<string, unknown[]> | unknown[] | null | undefined) ?? null;
        if (catchesData && typeof catchesData === 'object' && !Array.isArray(catchesData)) {
          const d = catchesData as Record<string, unknown>;
          if (Array.isArray(d.results)) catchesData = d.results;
          else if (Array.isArray(d.data)) catchesData = d.data;
          else if (Array.isArray(d.catches)) catchesData = d.catches;
          else if (Array.isArray(d.items)) catchesData = d.items;
        }
        const count =
          catchesData === null
            ? 0
            : Array.isArray(catchesData)
              ? catchesData.length
              : typeof catchesData === 'object'
                ? Object.keys(catchesData).length
                : 0;
        setTrawlCatches(catchesData);
        const filtered = filterTrawlCatchesBySpecies(catchesData);
        const forMap = { filtered, unfiltered: catchesData };
        onTrawlCatchesChange?.(forMap);
        if (mapController?.isReady()) {
          await mapController.setTrawlSurveyData(forMap);
          if (!mapController.isLayerActive('trawl-survey')) {
            await mapController.addLayer('trawl-survey');
          }
        }
      } catch (e) {
        if (!isMounted) return;
        setTrawlCatches(null);
        console.error('Failed to load trawl survey catches by cruise', e);
        onTrawlCatchesChange?.(null);
        if (mapController?.isReady()) {
          await mapController.setTrawlSurveyData(null);
        }
      } finally {
        if (isMounted) setTrawlCatchesLoading(false);
      }
    };
    loadCatches();
    return () => {
      isMounted = false;
    };
  }, [
    isTrawlSelectionValid,
    trawlCruises,
    trawlSeason,
    trawlYear,
    getTrawlSurveyCatchesByCruise,
    selectedCruise6,
    trawlCatches,
    onTrawlCatchesChange,
    filterTrawlCatchesBySpecies,
    mapController,
  ]);

  useEffect(() => {
    if (!trawlCatches || !isTrawlSelectionValid) return;
    const filtered = filterTrawlCatchesBySpecies(trawlCatches);
    const forMap = { filtered, unfiltered: trawlCatches };
    onTrawlCatchesChange?.(forMap);
    if (mapController?.isReady()) {
      mapController.setTrawlSurveyData(forMap);
      if (!mapController.isLayerActive('trawl-survey')) {
        mapController.addLayer('trawl-survey');
      }
    }
  }, [
    selectedSpeciesUuid,
    trawlCatches,
    isTrawlSelectionValid,
    filterTrawlCatchesBySpecies,
    onTrawlCatchesChange,
    mapController,
  ]);

  const loading =
    dataLoading ||
    catchabilityLoading ||
    stockSummaryLoading ||
    trawlCruisesLoading ||
    trawlCatchesLoading;
  const disabled = loading || !trawlCruises;

  return (
    <div className="control-section">
      <div className="section-title">
        <span className="section-icon">Trawl Survey Data</span>
      </div>
      <div className="form-group" style={{ marginBottom: '0px' }}>
        <div style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
          <div style={{ width: '50%', minWidth: 0 }}>
            <label className="form-label" style={{ width: '100%' }} htmlFor="trawlSeasonSelect">
              Season
            </label>
            <select
              id="trawlSeasonSelect"
              className="form-control"
              style={{ width: '100%', minWidth: 0 }}
              value={trawlSeason}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setTrawlSeason(e.target.value)}
              disabled={disabled}
            >
              <option value="">None</option>
              {availableSeasons.map((season) => {
                const labels: Record<string, string> = {
                  WINTER: 'Winter',
                  SPRING: 'Spring',
                  SUMMER: 'Summer',
                  FALL: 'Fall',
                };
                return (
                  <option key={season} value={season}>
                    {labels[season] ?? season}
                  </option>
                );
              })}
            </select>
          </div>
          <div style={{ width: '50%', minWidth: 0 }}>
            <label className="form-label" htmlFor="trawlYearSelect">
              Year
            </label>
            <select
              id="trawlYearSelect"
              className="form-control"
              style={{ width: '100%', minWidth: 0 }}
              value={trawlYear}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setTrawlYear(e.target.value)}
              disabled={disabled}
            >
              <option value="">None</option>
              {availableYears.map((year) => (
                <option key={year} value={year.toString()}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>

        {!trawlCruisesLoading && trawlCruises && trawlSeason && trawlYear && !isTrawlSelectionValid && (
          <div style={{ color: '#dc2626', fontSize: '11px', marginTop: '6px' }}>
            No cruise found for the selected season and year.
          </div>
        )}

        {isTrawlSelectionValid && (
          <>
            <div style={{ borderBottom: '1px solid rgb(206, 206, 206)', paddingBottom: '12px', marginTop: '12px' }} />
            <div className="form-group" style={{ marginTop: '18px' }}>
              <label className="form-label" htmlFor="trawlSpeciesSelect">
                Species
              </label>
              <select
                id="trawlSpeciesSelect"
                className="form-control"
                value={selectedSpeciesUuid}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedSpeciesUuid(e.target.value)}
                disabled={trawlSurveySpeciesLoading || dataLoading || catchabilityLoading || stockSummaryLoading}
              >
                <option value="">All species</option>
                {[...trawlSurveySpecies]
                  .sort((a, b) => (a.com_name ?? a.sci_name ?? '').localeCompare(b.com_name ?? b.sci_name ?? ''))
                  .map((s) => (
                    <option key={s.species_uuid} value={s.species_uuid}>
                      {s.com_name ?? s.sci_name ?? s.species_uuid}
                    </option>
                  ))}
              </select>
            </div>
            <div className="form-group" style={{ marginTop: '12px' }}>
              <label className="form-label" htmlFor="timeOfDaySelect">
                Time of Day
              </label>
              <select
                id="timeOfDaySelect"
                className="form-control"
                value={timeOfDayFilter}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setTimeOfDayFilter(e.target.value)}
                disabled={dataLoading || catchabilityLoading || stockSummaryLoading}
              >
                <option value="">All Times</option>
                <option value="Nighttime">Nighttime</option>
                <option value="Daytime">Daytime</option>
                <option value="Dusk">Dusk</option>
                <option value="Dawn">Dawn</option>
              </select>
            </div>
            <div className="form-group" style={{ marginTop: '12px' }}>
              <label className="form-label">Depth Range</label>
              <div style={{ padding: '14px 0px', width: '100%' }}>
                <RangeSlider
                  min={0}
                  max={200}
                  step={1}
                  value={undefined}
                  values={tempDepthRange}
                  onChange={(newValues: { min: number; max: number }) => setTempDepthRange(newValues)}
                  onInput={undefined}
                  onDragEnd={(newValues?: { min: number; max: number }) => {
                    const final = newValues ?? tempDepthRangeRef.current;
                    setDepthRange(final);
                  }}
                  disabled={dataLoading || catchabilityLoading || stockSummaryLoading}
                />
              </div>
              <div style={{ fontSize: '11px', color: '#6c757d', textAlign: 'center', marginTop: '4px' }}>
                Range: {tempDepthRange.min} - {tempDepthRange.max} meters
              </div>
            </div>
            <div className="form-group" style={{ marginTop: '12px' }}>
              <label className="form-label">Bottom Temperature Range</label>
              <div style={{ padding: '14px 0px', width: '100%' }}>
                <RangeSlider
                  min={-10}
                  max={30}
                  step={1}
                  value={undefined}
                  values={tempBotTempRange}
                  onChange={(newValues: { min: number; max: number }) => setTempBotTempRange(newValues)}
                  onInput={undefined}
                  onDragEnd={(newValues?: { min: number; max: number }) => {
                    const final = newValues ?? tempBotTempRangeRef.current;
                    setBotTempRange(final);
                  }}
                  disabled={dataLoading || catchabilityLoading || stockSummaryLoading}
                />
              </div>
              <div style={{ fontSize: '11px', color: '#6c757d', textAlign: 'center', marginTop: '4px' }}>
                Range: {tempBotTempRange.min} - {tempBotTempRange.max}°C
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
